import "./multiselect.css";
import {
  sellItem,
  shipItems,
  userVerificationEmail,
} from "../../api/modules/b2droppie.class";
import { useEffect, useState } from "react";
import { Card, Spinner } from "react-bootstrap";
import Toaster from "../../components/Toast/Toast";
import { useDispatch, useSelector } from "react-redux";
import { updateUserData } from "../../redux/slices/authSlice";
import { syncUserData } from "../../services/userSync.service";
import CommonModal from "../../components/CommonModal/CommonModal";

const MultiSelect = (props) => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [quantities, setQuantities] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const user = useSelector((state) => state?.auth?.user);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState({ success: "", error: "" });
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const [selectedItemIndexes, setSelectedItemIndexes] = useState([]);
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);

  useEffect(() => {
    if (!props.show || !props.data?.length) return;
    setData(props.data);
    const indexes = props.data.map((_, index) => index);
    setSelectedItemIndexes(indexes);
    setSelectAll(true);
    const initialQuantities = {};
    props.data.forEach((item) => {
      initialQuantities[item.id] = props.quantities?.[item.id] || 1;
    });
    setQuantities(initialQuantities);
  }, [props.show, props.data, props.quantities]);

  useEffect(() => {
    if (props.show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [props.show]);

  const handleCardClick = (index) => {
    const isItemSelected = selectedItemIndexes.includes(index);
    if (isItemSelected) {
      setSelectedItemIndexes(selectedItemIndexes.filter((i) => i !== index));
      setSelectAll(false);
    } else {
      const newIndexes = [...selectedItemIndexes, index];
      setSelectedItemIndexes(newIndexes);
      if (newIndexes.length === data.length) setSelectAll(true);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItemIndexes([]);
      setSelectAll(false);
    } else {
      setSelectedItemIndexes([...Array(data.length).keys()]);
      setSelectAll(true);
    }
  };

  useEffect(() => {
    const newTotalPrice = selectedItemIndexes.reduce((total, index) => {
      const item = data[index];
      const qty = quantities[item?.id] || 1;
      return total + parseFloat(item?.price || 0) * qty;
    }, 0);
    setTotalPrice(newTotalPrice);
  }, [selectedItemIndexes, quantities, data]);

  const handleSubmit = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const selectedItems = selectedItemIndexes.map((index) => data[index]);

      const payload = {
        product_id: selectedItems.map((item) => item.id),
        quantities: selectedItems.reduce((acc, item) => {
          acc[item.id] = quantities[item.id] || 1;
          return acc;
        }, {}),
      };
      const apiCall = props.actionType === "ship" ? shipItems : sellItem;
      const response = await apiCall(payload);

      if (!response?.data?.success) {
        props.setCartToast({
          success: "",
          error:
            response?.data?.error ||
            response?.data?.message ||
            response?.message ||
            "Something went wrong",
        });
        return;
      }

      props.setCartToast({
        success: response?.data?.message || "Action completed successfully",
        error: "",
      });

      const updated = await syncUserData();
      if (updated) {
        dispatch(updateUserData(updated));
      }

      props.refreshCart?.();
      setSelectedItemIndexes([]);
      setSelectAll(false);
      props.onHide();
    } catch (err) {
      if (err?.status === 403) {
        setShowVerifyEmailModal(true);
      }
      props.setCartToast({
        success: "",
        error:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Something went wrong",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const totalSelectedQuantity = selectedItemIndexes.reduce((total, index) => {
    const item = data[index];
    return total + (quantities[item?.id] || 1);
  }, 0);

  const userEmailVerification = async () => {
    try {
      const response = await userVerificationEmail({
        email: user?.email,
      });
      setError({
        success: response?.data?.message || "",
        error: response?.data?.error?.message || "",
      });
      if (response?.data?.success) {
        setShowVerifyEmailModal(false);
        setTimeout(() => {
          props.onHide();
        }, 1000);
      }
    } catch (err) {
      console.error(err);
      setError({
        success: "",
        error: "Something went wrong. Try again.",
      });
    } finally {
      setLoading(false);
      setShowVerifyEmailModal(false);
      setTimeout(() => {
        props.onHide();
      }, 1000);
    }
  };

  if (!props.show) return null;

  return (
    <>
      <div
        onClick={props.onHide}
        className={`custom-modal-overlay ${isDarkMode ? "dark" : "light"}`}
      >
        <div
          className={`custom-modal ${isDarkMode ? "dark-mode" : "light-mode"}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Toaster message={error} />

          <div className="custom-modal-header d-space-between">
            <h5 className={`${isDarkMode ? "text-white" : "text-deep-purple"}`}>
              Select Items to {props.actionType === "ship" ? "Ship" : "Sell"}
            </h5>
            <button className="close-btn" onClick={props.onHide}>
              ✕
            </button>
          </div>

          <div className="custom-modal-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <label className="d-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  style={{ marginRight: "8px" }}
                />
                {selectAll ? "Unselect All" : "Select All"}
              </label>
              <div
                style={{ fontWeight: 600, color: isDarkMode ? "#bbb" : "#333" }}
              >
                Total: ${totalPrice.toFixed(2)}
              </div>
            </div>

            <div className="d-flex flex-wrap gap-3 justify-content-center">
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : data.length === 0 ? (
                <div className="text-center text-muted py-4">
                  No items available in cart.
                </div>
              ) : (
                data.map((item, index) => {
                  const selected = selectedItemIndexes.includes(index);
                  const currentQty = quantities[item.id] || 1;

                  return (
                    <Card
                      key={item.id}
                      onClick={() => handleCardClick(index)}
                      className={`item-card multi-select-items ${
                        selected ? "selected" : ""
                      }`}
                      style={{
                        border: selected
                          ? "1px solid #735800"
                          : "1px solid #ddd",
                        background: isDarkMode
                          ? selected
                            ? "#302B1A"
                            : "#302B1A"
                          : "#e8def7",
                      }}
                    >
                      <span className="multi-select-quantity-text">
                        {currentQty}
                      </span>
                      <div className="p-2 text-center">
                        <img
                          src={
                            item?.box_images
                              ? item.box_images.image
                              : item?.image || "placeholder.png"
                          }
                          alt="Item"
                          className="item-images"
                        />
                      </div>

                      <Card.Body className="text-center py-2">
                        <Card.Title
                          style={{
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            color: isDarkMode ? "#fff" : "#f6f6f6",
                          }}
                        >
                          {item?.name || "Voucher"}
                        </Card.Title>
                        <Card.Text
                          style={{
                            color: isDarkMode ? "#bbb" : "#555",
                            fontSize: "0.85rem",
                          }}
                        >
                          ${item?.price}
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  );
                })
              )}
            </div>
          </div>

          <div className="custom-modal-footer d-space-between">
            <div
              className={`selected-item-count-price ${isDarkMode ? "text-white" : "text-deep-purple"}`}
            >
              {totalSelectedQuantity} selected — ${totalPrice.toFixed(2)}
            </div>
            <div className="d-flex gap-2">
              <button
                onClick={handleSubmit}
                className="filled-medium-deep-yellow-btn-dark text-nowrap"
                disabled={!selectedItemIndexes.length || isProcessing}
              >
                {isProcessing
                  ? "Processing..."
                  : props.actionType === "ship"
                    ? "Ship Selected"
                    : "Sell Selected"}
              </button>
              <button
                onClick={props.onHide}
                disabled={isProcessing}
                className="deep-primary-monochrome-btn-dark text-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <CommonModal
        show={showVerifyEmailModal}
        onClose={() => setShowVerifyEmailModal(false)}
        onConfirm={() => userEmailVerification()}
        title={
          <h4
            className={`${isDarkMode ? "text-light-lavender-purple" : "text-deep-purple"} w-100 d-center`}
          >
            Email not Verified!
          </h4>
        }
        body={
          <>
            <p
              className={`${isDarkMode ? "text-muter-lavender-purple" : "text-charcoal-gray"} mb-2`}
            >
              Please verify your email address. Click Here to generate an email.
            </p>
          </>
        }
        showFooter={true}
        modalWidth="w-lg-35"
        isDarkMode={isDarkMode}
        confirmText="Send Email!"
        btnStyle="filled-danger-btn-dark"
      />
    </>
  );
};

export default MultiSelect;
